import React, { useEffect, useState } from 'react';
import { Spin, Select, Cascader, Modal } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import ExchangeIcon from '../assets/icons/exchange.svg';
import VelocitaIcon from '../assets/icons/ElementVelocità.svg';
import CongestionIcon from '../assets/Congestion.svg';
import AnomalyIcon from '../assets/Anomaly.svg';
import RumorositaIcon from '../assets/Rumorosità.svg';
import VibrazioneIcon from '../assets/icons/Vibrazione.svg';
import MediaIcon from '../assets/icons/Media.svg';
import '../styles/ConfrontoIMUGraph.css';
import { fetchEnvironmentData, fetchEnvironmentFileIndex, fetchNoiseData, fetchNoiseDailyData, fetchVibrationData, fetchEnvironmentDailyData } from '../APIs/apiService';

const ConfrontoIMUGraph = ({arco, radarSelected}) => {
  console.log("🚀 ~ ConfrontoIMUGraph ~ arco:", arco)
  console.log("🚀 ~ ConfrontoIMUGraph ~ radarSelected:", radarSelected)
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRoadDate, setSelectedRoadDate] = useState(null);
  const [selectedRadarDate, setSelectedRadarDate] = useState(null);
  const [selectedFactors, setSelectedFactors] = useState([null, null]);
  const [selectedFactorPaths, setSelectedFactorPaths] = useState([null, null]); // Store cascader paths
  const [roadDataOptions, setRoadDataOptions] = useState([]);
  const [sensorDataOptions, setSensorDataOptions] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine display mode
  const hasRoads = arco && arco.length > 0;
  const hasMultipleRoads = arco && arco.length >= 2;
  const isSingleRoad = hasRoads && arco.length === 1 && !radarSelected;
  const isSingleRadar = radarSelected && (!arco || arco.length === 0);
  const isComparison = (hasMultipleRoads && !radarSelected) || (hasRoads && radarSelected);

  // Build options for Cascader (parent-child structure)
  useEffect(() => {
    // Define categories structure for road metrics
    const roadCategories = [
      {
        value: 'velocita',
        label: 'Velocità',
        key: 'velocita',
        field: 'velocita_road1',
        sensor: 'calculated'
      },
      {
        value: 'congestion',
        label: 'Congestion',
        key: 'congestion',
        field: 'congestion_road1',
        sensor: 'calculated'
      },
      {
        value: 'anomaly',
        label: 'Anomaly',
        key: 'anomaly',
        field: 'anomaly_road1',
        sensor: 'calculated'
      }
    ];

    // Define categories structure for radar metrics
    const radarCategories = [
      {
        value: 'rumorosita',
        label: 'Rumorosità',
        key: 'radar_rumorosita',
        children: [
          { value: 'radar_rumorosita_db', key: 'radar_rumorosita_db', label: 'Rumorosità (dB)', field: 'db', sensor: 'noise' }
        ]
      },
      {
        value: 'vibrazione',
        label: 'Vibrazione',
        key: 'radar_vibrazione',
        children: [
          { value: 'radar_vibrazione_total', key: 'radar_vibrazione_total', label: 'RMS(a) (g)', field: 'radar_vibrazione_total', sensor: 'Vibration' },
          { value: 'radar_vibrazione_x', key: 'radar_vibrazione_x', label: 'RMS(ax) (g)', field: 'radar_vibrazione_x', sensor: 'Vibration' },
          { value: 'radar_vibrazione_y', key: 'radar_vibrazione_y', label: 'RMS(ay) (g)', field: 'radar_vibrazione_y', sensor: 'Vibration' },
          { value: 'radar_vibrazione_z', key: 'radar_vibrazione_z', label: 'RMS(az) (g)', field: 'radar_vibrazione_z', sensor: 'Vibration' }
        ]
      },
      {
        value: 'media_ambientale',
        label: 'Media giornaliera ambientale',
        key: 'radar_media_ambientale',
        children: [
          { value: 'radar_temperatura', key: 'radar_temperatura', label: 'Temperatura (°C)', field: 'radar_temperature', sensor: 'Environment' },
          { value: 'radar_umidita', key: 'radar_umidita', label: 'Umidità (%)', field: 'radar_humidity', sensor: 'Environment' },
          { value: 'radar_pressione', key: 'radar_pressione', label: 'Pressione (hPa)', field: 'radar_pressure', sensor: 'Environment' },
          { value: 'radar_formaldeide', key: 'radar_formaldeide', label: 'Formaldeide (ppb)', field: 'radar_formaldehyde_ug_m3', sensor: 'Environment' },
          { value: 'radar_pm25', key: 'radar_pm25', label: 'PM₂.₅ (µg/m³)', field: 'radar_pm2p5_ug_m3', sensor: 'Environment' },
          { value: 'radar_no2', key: 'radar_no2', label: 'NO₂ (ppm)', field: 'radar_no2_ppm', sensor: 'Environment' },
          { value: 'radar_c2h5ch', key: 'radar_c2h5ch', label: 'C₂H₅CH (ppm)', field: 'radar_c2h5ch_ppm', sensor: 'Environment' },
          { value: 'radar_voc', key: 'radar_voc', label: 'VOC (ppm)', field: 'radar_voc_ppm', sensor: 'Environment' }
        ]
      }
    ];

    // Determine which options to use based on mode
    const hasRoad1 = arco && arco.length > 0;
    const hasRoad2 = arco && arco.length >= 2 && !radarSelected;
    const hasRadar = radarSelected;

    // Options for Dropdown 1
    let road1Options = [];
    if (hasRoad1 && !radarSelected) {
      road1Options = roadCategories;
    } else if (hasRoad1 && hasRadar) {
      road1Options = roadCategories;
    } else if (isSingleRadar) {
      road1Options = radarCategories;
    }

    // Options for Dropdown 2
    let road2Options = [];
    if (hasRadar && hasRoads) {
      road2Options = radarCategories;
    } else if (hasRoad2) {
      road2Options = roadCategories.map(cat => ({
        ...cat,
        field: cat.field?.replace('_road1', '_road2') || cat.field,
        children: cat.children?.map(child => ({
          ...child,
          field: child.field?.replace('_road1', '_road2') || child.field
        }))
      }));
    } else if (isSingleRoad) {
      // For single road, use same fields as road1 (velocita_road1, congestion_road1, etc.)
      road2Options = roadCategories;
    } else if (isSingleRadar) {
      road2Options = radarCategories;
    } else {
      road2Options = roadCategories;
    }

    setRoadDataOptions(road1Options);
    setSensorDataOptions(road2Options);
  }, [arco, radarSelected, isSingleRoad, isSingleRadar, hasRoads]);

  // Fetch available dates and initialize
  useEffect(() => {
    // Set default date to today
    const today = dayjs().format('YYYY-MM-DD');

    let streetDates = [];
    let radarDates = [];

    // Try to fetch radar dates from new format first using centralized API service
    fetchEnvironmentData()
      .then(data => {
        // Extract unique dates from new format
        radarDates = [...new Set(data.map(item => {
          const timestamp = new Date(parseInt(item.time) * 1000);
          return timestamp.toISOString().split('T')[0];
        }))].sort();

        // Merge street and radar dates
        mergeAndSetDates(streetDates, radarDates);
      })
      .catch(() => {
        // Fallback to old format
        console.warn('Failed to load Environment_1h.json, falling back to ambientali_finale_tutto');
        fetchEnvironmentFileIndex()
          .then(files => {
            if (Array.isArray(files)) {
              radarDates = files
                .map(file => {
                  // Extract date from filename like "20250501.json"
                  const match = file.match(/(\d{8})\.json/);
                  if (match) {
                    const dateStr = match[1];
                    return dayjs(dateStr, 'YYYYMMDD').format('YYYY-MM-DD');
                  }
                  return null;
                })
                .filter(Boolean)
                .sort();

              // Merge street and radar dates
              mergeAndSetDates(streetDates, radarDates);
            }
          })
          .catch(err => {
            console.warn('Could not load radar dates, using fallback dates', err);
            // Fallback radar dates
            radarDates = [];
            for (let day = 15; day <= 31; day++) {
              radarDates.push(`2025-04-${day.toString().padStart(2, '0')}`);
            }
            for (let day = 1; day <= 31; day++) {
              radarDates.push(`2025-05-${day.toString().padStart(2, '0')}`);
            }
            for (let day = 1; day <= 3; day++) {
              radarDates.push(`2025-06-${day.toString().padStart(2, '0')}`);
            }

            // Merge street and radar dates
            mergeAndSetDates(streetDates, radarDates);
          });
      });

    // Always fetch and set available street dates
    if (arco && arco.length > 0) {
      streetDates = [...new Set(arco.map(item => {
        // Support both new and old date formats
        if (item.timestamp_utc) {
          // New format: "2025-05-10 07:50:23+00:00"
          return item.timestamp_utc.split(' ')[0];
        } else if (item.date) {
          // Old format: "2024-11-22T00:00:00.000"
          return dayjs(item.date).format('YYYY-MM-DD');
        }
        return null;
      }).filter(Boolean))].sort();
    } else {
      // Fallback to a date range if no arco data
      streetDates = [];
      for (let day = 7; day <= 31; day++) {
        streetDates.push(`2025-05-${day.toString().padStart(2, '0')}`);
      }
      for (let day = 1; day <= 3; day++) {
        streetDates.push(`2025-06-${day.toString().padStart(2, '0')}`);
      }
    }

    // Helper function to merge and set all dates
    const mergeAndSetDates = (streets, radars) => {
      // Combine both street and radar dates, remove duplicates, and sort
      const allDates = [...new Set([...streets, ...radars])].sort();

      // Filter dates to show only 2025-05-10 to 2025-05-18
      const startDate = '2025-05-10';
      const endDate = '2025-05-18';
      const filteredDates = allDates.filter(date => date >= startDate && date <= endDate);

      setAvailableDates(filteredDates);

      // Set default date to first available date
      const defaultDate = filteredDates.length > 0 ? filteredDates[0] : today;

      setSelectedDate(defaultDate);
      setSelectedRoadDate(defaultDate);
      setSelectedRadarDate(defaultDate);
    };

    // Initial merge with street dates (radar dates will be merged when fetch completes)
    mergeAndSetDates(streetDates, radarDates);
  }, [arco]);

  // Fetch environmental data for comparison based on selected date
  useEffect(() => {
    // Determine which dates to use based on comparison mode
    const roadDateToUse = hasRoads && radarSelected ? selectedRoadDate : selectedDate;
    const radarDateToUse = hasRoads && radarSelected ? selectedRadarDate : selectedDate;

    // If only radar is selected (no roads)
    if (radarSelected && (!arco || arco.length === 0)) {
      if (!radarDateToUse) {
        setLoading(false);
        return;
      }

      setLoading(true);
      console.log('Loading radar-only data for date:', radarDateToUse);

      // Format date for file lookup (YYYYMMDD)
      const dateStr = radarDateToUse.replace(/-/g, '');

      // Load all sensor data for radar-only mode
      Promise.all([
        // Environmental data using centralized API service
        fetchEnvironmentData()
          .then(data => data.filter(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const itemDate = timestamp.toISOString().split('T')[0];
            return itemDate === radarDateToUse;
          }))
          .catch(() => []),
        // Noise data using centralized API service
        fetchNoiseData()
          .then(data => data.filter(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const itemDate = timestamp.toISOString().split('T')[0];
            return itemDate === radarDateToUse;
          }))
          .catch(() => []),
        // Vibration data using centralized API service
        fetchVibrationData()
          .then(data => data.filter(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const itemDate = timestamp.toISOString().split('T')[0];
            return itemDate === radarDateToUse;
          }))
          .catch(() => [])
      ]).then(([envData, noiseData, vibrationData]) => {
        const timeSeriesData = {};

        // Process environmental data
        if (envData && envData.length > 0) {
          envData.forEach(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }

            timeSeriesData[timeKey].radar_temperature = parseFloat(item['Temperatura [°C]']) || 0;
            timeSeriesData[timeKey].radar_humidity = parseFloat(item['Umidità [%]']) || 0;
            timeSeriesData[timeKey].radar_pressure = parseFloat(item['Pressione [hPa]']) || 0;
            timeSeriesData[timeKey].radar_pm2p5_ug_m3 = parseFloat(item['PM₂.₅ [µg/m³]']) || 0;
            timeSeriesData[timeKey].radar_no2_ppm = parseFloat(item['NO₂ [ppm]']) || 0;
            timeSeriesData[timeKey].radar_c2h5ch_ppm = parseFloat(item['C₂H₅CH [ppm]']) || 0;
            timeSeriesData[timeKey].radar_voc_ppm = parseFloat(item['VOC [ppm]']) || 0;
            timeSeriesData[timeKey].radar_formaldehyde_ug_m3 = parseFloat(item['Formaldeide [ppb]']) || 0;
          });
        }

        // Process noise data
        if (noiseData && noiseData.length > 0) {
          noiseData.forEach(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }
            timeSeriesData[timeKey].db = parseFloat(item['LZeq_dBFS']) || 0;
          });
        }

        // Process vibration data
        if (vibrationData && vibrationData.length > 0) {
          vibrationData.forEach(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }

            timeSeriesData[timeKey].radar_vibrazione_total = parseFloat(item['RMS(a) [g]']) || 0;
            timeSeriesData[timeKey].radar_vibrazione_x = parseFloat(item['RMS(ax) [g]']) || 0;
            timeSeriesData[timeKey].radar_vibrazione_y = parseFloat(item['RMS(ay) [g]']) || 0;
            timeSeriesData[timeKey].radar_vibrazione_z = parseFloat(item['RMS(az) [g]']) || 0;
          });
        }

        const chartData = Object.values(timeSeriesData).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
        console.log('Radar-only data loaded (new format):', chartData.length, 'time points');
        setData(chartData);
        setLoading(false);
      })
        .catch(() => {
          // Fallback to old format using centralized API service
          console.warn('Failed to load Environment_1h.json, falling back to old format');
          fetchEnvironmentDailyData(dateStr)
            .then(envData => {
              if (envData && envData.acquisizioni) {
                // Process radar acquisitions into time-series data
                const chartData = envData.acquisizioni.map(acq => ({
                  timestamp: acq.ora,
                  radar_co2_ppm: acq.valori?.SCD30?.co2_ppm?.v || 0,
                  radar_pm10_ug_m3: acq.valori?.SN5?.pm10_ug_m3?.v || 0,
                  radar_pm2p5_ug_m3: acq.valori?.SN5?.pm2p5_ug_m3?.v || 0,
                  radar_temperature: acq.valori?.SCD30?.temperature?.v || 0,
                  radar_humidity: acq.valori?.SCD30?.humidity?.v || 0,
                  radar_pressure: acq.valori?.BME688?.atmospheric_pressure?.v || 0,
                  radar_no2_ppb: acq.valori?.MultiChannelGas?.NO2_PPM?.v
                    ? acq.valori.MultiChannelGas.NO2_PPM.v * 1000
                    : 0,
                })).sort((a, b) => a.timestamp.localeCompare(b.timestamp));

                console.log('Radar-only data loaded (old format):', chartData.length, 'time points');
                setData(chartData);
              } else {
                console.warn('No radar data available for date:', radarDateToUse);
                setData([]);
              }
              setLoading(false);
            })
            .catch(err => {
              console.error('Error loading radar data:', err);
              setData([]);
              setLoading(false);
            });
        });

      return;
    }

    if (!roadDateToUse || !arco || arco.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);

    console.log('Selected road date:', roadDateToUse);
    console.log('Selected radar date:', radarDateToUse);
    console.log('Total arco elements:', arco.length);
    console.log('Radar selected:', radarSelected);

    // Format date for file lookup (YYYYMMDD)
    const roadDateStr = roadDateToUse.replace(/-/g, '');
    const radarDateStr = radarDateToUse.replace(/-/g, '');

    // Filter arco data by selected road date - support both formats
    const filteredArco = arco.filter(element => {
      if (element.timestamp_utc) {
        // New format
        const elementDate = element.timestamp_utc.split(' ')[0];
        return elementDate === roadDateToUse;
      } else if (element.date) {
        // Old format
        const elementDate = dayjs(element.date).format('YYYY-MM-DD');
        return elementDate === roadDateToUse;
      }
      return false;
    });

    console.log(`Filtered ${filteredArco.length} elements for date ${roadDateToUse}`);

    // If comparing with radar and no road data, we can still show radar data
    if (filteredArco.length === 0 && !radarSelected) {
      console.warn('No road data available for selected date');
      setData([]);
      setLoading(false);
      return;
    }

    // If no road data but radar is selected, continue to fetch radar data
    if (filteredArco.length === 0 && radarSelected) {
      console.log('No road data, but radar is selected. Will fetch radar data only.');
    }

    // Group filtered arco data by hash (road identifier)
    const roadsByHash = {};
    filteredArco.forEach(element => {
      const hash = element.hash;
      if (!roadsByHash[hash]) {
        roadsByHash[hash] = [];
      }
      roadsByHash[hash].push(element);
    });

    // Get the unique road hashes
    const roadHashes = Object.keys(roadsByHash);

    // Validation: we need either 2 roads, or 1 road + radar, or just data from at least one source
    if (!radarSelected && roadHashes.length < 2) {
      console.warn('Need at least 2 different roads (hash values) for comparison without radar');
      // Don't return yet - check if we can proceed with partial data
    }

    // Allow proceeding if we have radar selected (even without road data)
    const road1Hash = roadHashes.length > 0 ? roadHashes[0] : null;
    const road2Hash = (!radarSelected && roadHashes.length >= 2) ? roadHashes[1] : null;
    const road1Data = road1Hash ? roadsByHash[road1Hash] : [];
    const road2Data = road2Hash ? roadsByHash[road2Hash] : [];

    if (road1Hash) {
      console.log(`Road 1 (${road1Hash}): ${road1Data.length} data points`);
    }
    if (road2Hash) {
      console.log(`Road 2 (${road2Hash}): ${road2Data.length} data points`);
    }
    if (radarSelected) {
      console.log('Radar selected - will fetch radar data');
    }

    // Helper function to calculate velocity from arco data
    const calculateVelocity = (element) => {
      if (!element) return 0;

      // Check if new format has speed directly
      if (element.speed) {
        return parseFloat(element.speed);
      }

      // Old format: calculate from distance / time
      const length = parseFloat(element.length_in_meters);
      const time = parseFloat(element.travel_time_in_seconds);

      if (length && time && time > 0) {
        // Convert to km/h: (meters/seconds) * 3.6
        return (length / time) * 3.6;
      }
      return 0;
    };

    // Helper function to calculate anomaly score based on traffic conditions
    const calculateAnomaly = (element) => {
      if (!element) return 0;

      // Check if new format has anomaly directly
      if (element.anomaly !== undefined) {
        return parseFloat(element.anomaly);
      }

      // Old format: calculate from travel times
      const travelTime = parseFloat(element.travel_time_in_seconds);
      const noTrafficTime = parseFloat(element.no_traffic_travel_time_in_seconds);

      if (travelTime && noTrafficTime && noTrafficTime > 0) {
        const delay = travelTime - noTrafficTime;
        const anomalyScore = delay > 0 ? (delay / noTrafficTime) * 100 : 0;
        return Math.min(anomalyScore, 100); // Cap at 100
      }
      return 0;
    };

    // Helper function to calculate congestion level
    const calculateCongestion = (element) => {
      if (!element) return 0;

      // Check if new format has congestion directly
      if (element.congestion !== undefined) {
        return parseFloat(element.congestion);
      }

      // Old format: calculate from traffic delay and length
      const trafficDelay = parseFloat(element.traffic_delay_in_seconds) || 0;
      const trafficLength = parseFloat(element.traffic_length_in_meters) || 0;
      const roadLength = parseFloat(element.length_in_meters) || 1;

      if (trafficDelay > 0 || trafficLength > 0) {
        const delayFactor = trafficDelay / 10; // Normalize
        const lengthFactor = (trafficLength / roadLength) * 100;
        return Math.min((delayFactor + lengthFactor) / 2, 100);
      }
      return 0;
    };

    // Helper function to process road data into time series
    const processRoadDataByTime = (roadData) => {
      const timeData = {};

      // Log first element to understand data structure
      if (roadData.length > 0) {
        console.log('Sample road element:', {
          hour: roadData[0].hour,
          timestamp: roadData[0].timestamp,
          datetime: roadData[0].datetime,
          date: roadData[0].date,
          travel_time: roadData[0].travel_time_in_seconds,
          length: roadData[0].length_in_meters
        });
      }

      roadData.forEach((element, index) => {
        // Use the hour field if available, otherwise fall back to timestamp
        let timeKey;

        // New format: check for time field (Unix timestamp as string)
        if (element.time && !element.timestamp) {
          const unixTime = parseInt(element.time);
          timeKey = dayjs.unix(unixTime).format('HH:mm');
        } else if (element.timestamp_utc) {
          // New format: "2025-05-10 07:50:23+00:00"
          timeKey = element.timestamp_utc.split(' ')[1].substring(0, 5); // Get HH:mm
        } else if (element.timestamp) {
          // Old format: Unix timestamp
          timeKey = dayjs.unix(element.timestamp).format('HH:mm');
        } else if (element.hour !== undefined && element.hour !== null) {
          // Convert hour to HH:mm format
          if (element.hour < 24) {
            timeKey = `${element.hour.toString().padStart(2, '0')}:00`;
          } else {
            // Try parsing hour as datetime
            timeKey = dayjs(element.datetime || element.hour).format('HH:mm');
          }
        } else if (element.datetime) {
          timeKey = dayjs(element.datetime).format('HH:mm');
        } else {
          console.warn('Skipping element without time info:', index);
          return; // Skip this element if no time information
        }

        if (!timeData[timeKey]) {
          timeData[timeKey] = [];
        }
        timeData[timeKey].push(element);
      });

      // Average data points for each time slot
      const averagedData = {};
      Object.keys(timeData).forEach(timeKey => {
        const elements = timeData[timeKey];

        // Calculate averages
        const velocities = elements.map(e => calculateVelocity(e));
        const anomalies = elements.map(e => calculateAnomaly(e));
        const congestions = elements.map(e => calculateCongestion(e));

        const avgVelocita = velocities.reduce((a, b) => a + b, 0) / velocities.length;
        const avgAnomaly = anomalies.reduce((a, b) => a + b, 0) / anomalies.length;
        const avgCongestion = congestions.reduce((a, b) => a + b, 0) / congestions.length;

        averagedData[timeKey] = {
          velocita: avgVelocita,
          anomaly: avgAnomaly,
          congestion: avgCongestion,
          vibrazione: (avgVelocita * 0.3 + avgCongestion * 0.7) / 10
        };
      });

      return averagedData;
    };

    // Process road data (only if we have road data)
    const road1TimeSeries = road1Data.length > 0 ? processRoadDataByTime(road1Data) : {};
    const road2TimeSeries = road2Data.length > 0 ? processRoadDataByTime(road2Data) : {};

    if (road1Data.length > 0) {
      console.log('Road 1 time series points:', Object.keys(road1TimeSeries).length);
      console.log('Sample Road 1 data:', Object.values(road1TimeSeries)[0]);
    }
    if (road2Data.length > 0) {
      console.log('Road 2 time series points:', Object.keys(road2TimeSeries).length);
      console.log('Sample Road 2 data:', Object.values(road2TimeSeries)[0]);
    }

    // Create base time series data from road data
    const timeSeriesData = {};

    // If we have no road data but radar is selected, create a single time point for the date
    if (Object.keys(road1TimeSeries).length === 0 && Object.keys(road2TimeSeries).length === 0 && radarSelected) {
      // Create a single entry for the selected date
      timeSeriesData[selectedDate] = { timestamp: selectedDate };
    } else {
      // Add road-specific time points
      [...Object.keys(road1TimeSeries), ...Object.keys(road2TimeSeries)].forEach(timeKey => {
        if (!timeSeriesData[timeKey]) {
          timeSeriesData[timeKey] = { timestamp: timeKey };
        }

        // Add Road 1 data
        if (road1TimeSeries[timeKey]) {
          timeSeriesData[timeKey].velocita_road1 = road1TimeSeries[timeKey].velocita;
          timeSeriesData[timeKey].anomaly_road1 = road1TimeSeries[timeKey].anomaly;
          timeSeriesData[timeKey].congestion_road1 = road1TimeSeries[timeKey].congestion;
          timeSeriesData[timeKey].vibrazione_road1 = road1TimeSeries[timeKey].vibrazione;
        } else {
          timeSeriesData[timeKey].velocita_road1 = 0;
          timeSeriesData[timeKey].anomaly_road1 = 0;
          timeSeriesData[timeKey].congestion_road1 = 0;
          timeSeriesData[timeKey].vibrazione_road1 = 0;
        }

        // Add Road 2 data
        if (road2TimeSeries[timeKey]) {
          timeSeriesData[timeKey].velocita_road2 = road2TimeSeries[timeKey].velocita;
          timeSeriesData[timeKey].anomaly_road2 = road2TimeSeries[timeKey].anomaly;
          timeSeriesData[timeKey].congestion_road2 = road2TimeSeries[timeKey].congestion;
          timeSeriesData[timeKey].vibrazione_road2 = road2TimeSeries[timeKey].vibrazione;
        } else {
          timeSeriesData[timeKey].velocita_road2 = 0;
          timeSeriesData[timeKey].anomaly_road2 = 0;
          timeSeriesData[timeKey].congestion_road2 = 0;
          timeSeriesData[timeKey].vibrazione_road2 = 0;
        }
      });
    }

    // Fetch environmental data, noise data, and vibration data - try new format first using centralized API service
    Promise.all([
      // Environmental data - try new format first
      fetchEnvironmentData()
        .then(data => {
          // Filter by selected radar date
          return data.filter(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const itemDate = timestamp.toISOString().split('T')[0];
            return itemDate === radarDateToUse;
          });
        })
        .catch(() => {
          // Fallback to old format
          return fetchEnvironmentDailyData(radarDateStr)
            .catch(() => null);
        }),
      // Noise data - try new format first
      fetchNoiseData()
        .then(data => {
          // Filter by selected radar date
          return data.filter(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const itemDate = timestamp.toISOString().split('T')[0];
            return itemDate === radarDateToUse;
          });
        })
        .catch(() => {
          // Fallback to old format
          return fetchNoiseDailyData(roadDateToUse)
            .catch(() => null);
        }),
      // Vibration data - new format only
      fetchVibrationData()
        .then(data => {
          // Filter by selected radar date
          return data.filter(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const itemDate = timestamp.toISOString().split('T')[0];
            return itemDate === radarDateToUse;
          });
        })
        .catch(() => null)
    ]).then(([envData, noiseData, vibrationData]) => {
      // Process environmental acquisitions - support both new and old formats
      if (envData) {
        if (Array.isArray(envData)) {
          // New format: array of objects
          envData.forEach(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }

            // Map new format fields to old format field names
            if (item['Temperatura [°C]']) timeSeriesData[timeKey].temperature = parseFloat(item['Temperatura [°C]']);
            if (item['Umidità [%]']) timeSeriesData[timeKey].humidity = parseFloat(item['Umidità [%]']);
            if (item['Pressione [hPa]']) timeSeriesData[timeKey].atmospheric_pressure = parseFloat(item['Pressione [hPa]']);
            if (item['PM₂.₅ [µg/m³]']) timeSeriesData[timeKey].pm2p5_ug_m3 = parseFloat(item['PM₂.₅ [µg/m³]']);
            if (item['NO₂ [ppm]']) timeSeriesData[timeKey].NO2_PPM = parseFloat(item['NO₂ [ppm]']);
            if (item['C₂H₅CH [ppm]']) timeSeriesData[timeKey].c2h5ch_ppm = parseFloat(item['C₂H₅CH [ppm]']);
            if (item['VOC [ppm]']) timeSeriesData[timeKey].voc_ppm = parseFloat(item['VOC [ppm]']);
            if (item['Formaldeide [ppb]']) timeSeriesData[timeKey].formaldehyde_ug_m3 = parseFloat(item['Formaldeide [ppb]']);
          });
        } else if (envData.acquisizioni) {
          // Old format: object with acquisizioni array
          envData.acquisizioni.forEach(acq => {
            const timeKey = acq.ora;
            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }

            // Extract sensor values
            if (acq.valori) {
              Object.keys(acq.valori).forEach(sensorKey => {
                const sensorData = acq.valori[sensorKey];
                Object.keys(sensorData).forEach(metric => {
                  if (sensorData[metric].v !== undefined) {
                    timeSeriesData[timeKey][metric] = sensorData[metric].v;
                  }
                });
              });
            }
          });
        }
      }

      // Process noise data - support both new and old formats
      if (noiseData) {
        if (Array.isArray(noiseData)) {
          // New format: array of objects
          noiseData.forEach(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }
            timeSeriesData[timeKey].db = parseFloat(item['LZeq_dBFS']);
          });
        } else if (noiseData.data && Array.isArray(noiseData.data)) {
          // Old format: object with data array
          noiseData.data.forEach(item => {
            const timeKey = item.time.substring(0, 5); // Get HH:mm
            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }
            timeSeriesData[timeKey].db = item.db;
          });
        }
      }

      // Process vibration data - new format only
      if (vibrationData && Array.isArray(vibrationData)) {
        vibrationData.forEach(item => {
          const timestamp = new Date(parseInt(item.time) * 1000);
          const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

          if (!timeSeriesData[timeKey]) {
            timeSeriesData[timeKey] = { timestamp: timeKey };
          }

          // Add vibration data with radar_ prefix
          timeSeriesData[timeKey].radar_vibrazione_total = parseFloat(item['RMS(a) [g]']) || 0;
          timeSeriesData[timeKey].radar_vibrazione_x = parseFloat(item['RMS(ax) [g]']) || 0;
          timeSeriesData[timeKey].radar_vibrazione_y = parseFloat(item['RMS(ay) [g]']) || 0;
          timeSeriesData[timeKey].radar_vibrazione_z = parseFloat(item['RMS(az) [g]']) || 0;
        });
      }

      // Process radar data if radar is selected - support both formats
      if (radarSelected && envData) {
        console.log('Processing radar data');

        if (Array.isArray(envData)) {
          // New format: array of environmental data
          envData.forEach(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

            // Create time entry if it doesn't exist
            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }

            // Extract radar sensor values with radar_ prefix from new format
            if (item['Temperatura [°C]']) timeSeriesData[timeKey].radar_temperature = parseFloat(item['Temperatura [°C]']);
            if (item['Umidità [%]']) timeSeriesData[timeKey].radar_humidity = parseFloat(item['Umidità [%]']);
            if (item['Pressione [hPa]']) timeSeriesData[timeKey].radar_pressure = parseFloat(item['Pressione [hPa]']);
            if (item['PM₂.₅ [µg/m³]']) timeSeriesData[timeKey].radar_pm2p5_ug_m3 = parseFloat(item['PM₂.₅ [µg/m³]']);
            if (item['NO₂ [ppm]']) timeSeriesData[timeKey].radar_no2_ppm = parseFloat(item['NO₂ [ppm]']);
            if (item['C₂H₅CH [ppm]']) timeSeriesData[timeKey].radar_c2h5ch_ppm = parseFloat(item['C₂H₅CH [ppm]']);
            if (item['VOC [ppm]']) timeSeriesData[timeKey].radar_voc_ppm = parseFloat(item['VOC [ppm]']);
            if (item['Formaldeide [ppb]']) timeSeriesData[timeKey].radar_formaldehyde_ug_m3 = parseFloat(item['Formaldeide [ppb]']);
          });
        } else if (envData.acquisizioni) {
          // Old format: object with acquisizioni array
          envData.acquisizioni.forEach(acq => {
            const timeKey = acq.ora; // HH:mm format

            // Create time entry if it doesn't exist
            if (!timeSeriesData[timeKey]) {
              timeSeriesData[timeKey] = { timestamp: timeKey };
            }

            // Extract radar sensor values with radar_ prefix
            if (acq.valori) {
              // CO2 from SCD30
              if (acq.valori.SCD30?.co2_ppm?.v !== undefined) {
                timeSeriesData[timeKey].radar_co2_ppm = acq.valori.SCD30.co2_ppm.v;
              }

              // PM10 and PM2.5 from SN5
              if (acq.valori.SN5?.pm10_ug_m3?.v !== undefined) {
                timeSeriesData[timeKey].radar_pm10_ug_m3 = acq.valori.SN5.pm10_ug_m3.v;
              }
              if (acq.valori.SN5?.pm2p5_ug_m3?.v !== undefined) {
                timeSeriesData[timeKey].radar_pm2p5_ug_m3 = acq.valori.SN5.pm2p5_ug_m3.v;
              }

              // Temperature and humidity from SCD30
              if (acq.valori.SCD30?.temperature?.v !== undefined) {
                timeSeriesData[timeKey].radar_temperature = acq.valori.SCD30.temperature.v;
              }
              if (acq.valori.SCD30?.humidity?.v !== undefined) {
                timeSeriesData[timeKey].radar_humidity = acq.valori.SCD30.humidity.v;
              }

              // Pressure from BME688
              if (acq.valori.BME688?.atmospheric_pressure?.v !== undefined) {
                timeSeriesData[timeKey].radar_pressure = acq.valori.BME688.atmospheric_pressure.v;
              }

              // NO2 from MultiChannelGas (convert PPM to PPB)
              if (acq.valori.MultiChannelGas?.NO2_PPM?.v !== undefined) {
                timeSeriesData[timeKey].radar_no2_ppb = acq.valori.MultiChannelGas.NO2_PPM.v * 1000;
              }
            }
          });
        }

        console.log('Radar data processed, time points:', Object.keys(timeSeriesData).length);
      }

      // Convert to array and sort by time
      const chartData = Object.values(timeSeriesData)
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

      console.log('Chart data prepared:', chartData.length, 'points');
      console.log('Sample data point:', chartData[0]);
      console.log('Available fields:', chartData[0] ? Object.keys(chartData[0]) : []);

      // Log some actual values to verify data
      if (chartData.length > 0) {
        console.log('Road 1 velocita range:', {
          min: Math.min(...chartData.map(d => d.velocita_road1 || 0)),
          max: Math.max(...chartData.map(d => d.velocita_road1 || 0)),
          avg: chartData.reduce((sum, d) => sum + (d.velocita_road1 || 0), 0) / chartData.length
        });
        console.log('Road 2 velocita range:', {
          min: Math.min(...chartData.map(d => d.velocita_road2 || 0)),
          max: Math.max(...chartData.map(d => d.velocita_road2 || 0)),
          avg: chartData.reduce((sum, d) => sum + (d.velocita_road2 || 0), 0) / chartData.length
        });
      }

      setData(chartData);
      setLoading(false);
    }).catch(err => {
      // Even if environmental data fails, use road data
      console.warn('Could not load environmental data, using road data only:', err);
      const chartData = Object.values(timeSeriesData)
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      setData(chartData);
      setLoading(false);
    });
  }, [selectedDate, selectedRoadDate, selectedRadarDate, arco, radarSelected, hasRoads]);

  // Handle factor selection for dropdown 1 (road data)
  const handleRoadDataChange = (option, valuePath) => {
    setSelectedFactors([option, selectedFactors[1]]);
    setSelectedFactorPaths([valuePath, selectedFactorPaths[1]]);
  };

  // Handle factor selection for dropdown 2 (sensor data)
  const handleSensorDataChange = (option, valuePath) => {
    setSelectedFactors([selectedFactors[0], option]);
    setSelectedFactorPaths([selectedFactorPaths[0], valuePath]);
  };


  if (loading) {
    return (
      <div className="proprieta-wrapper">
        <div className="confronto-title">
          <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
          <h3>Confronto</h3>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <Spin size="large" tip="Caricamento dati confronto..." />
        </div>
      </div>
    );
  }

  const hasChartData = data.length > 0 && ((isComparison && selectedFactors[0] && selectedFactors[1]) || ((isSingleRoad || isSingleRadar) && selectedFactors[0]));

  return (
    <>
    <div className="proprieta-wrapper">
        <div className="confronto-title">
          <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
          <h3>Confronto</h3>
        </div>

      <div className="confronto-content">
        <div className="confronto-date-row">
          <p className="confronto-description">
            {isComparison
              ? 'Puoi scegliere solo due fattori da confrontare.'
              : isSingleRoad
                ? 'Confronta due metriche diverse della stessa strada.'
                : isSingleRadar
                  ? 'Confronta due metriche diverse dello stesso radar.'
                  : 'Seleziona uno o più fattori da visualizzare.'}
          </p>

          {/* Single date picker that applies to both selected elements */}
          <Select
            placeholder="Seleziona data"
            className="confronto-date-picker"
            value={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              setSelectedRoadDate(date);
              setSelectedRadarDate(date);
            }}
            getPopupContainer={(trigger) => trigger.parentElement}
            popupStyle={{ zIndex: 9999 }}
            style={{  borderRadius : "20px" }}
          >
            {availableDates.map(date => (
              <Select.Option key={date} value={date}>
                {dayjs(date).format('DD/MM/YYYY')}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Selects for metrics selection */}
        <div className="confronto-dropdowns">
          <div className="dropdown-container">
            <label className="dropdown-label">
              {isSingleRoad
                ? 'Strada - Seleziona Metrica'
                : isSingleRadar
                  ? 'Multisensor station - Seleziona Metrica'
                  : radarSelected && arco && arco.length === 1
                    ? 'Strada - Seleziona Metrica'
                    : 'Strada 1 - Seleziona Metrica'}
            </label>
            <Cascader
              className="confronto-select"
              placeholder={
                isSingleRoad
                  ? 'Seleziona metrica per Strada'
                  : isSingleRadar
                    ? 'Seleziona metrica per Multisensor station'
                    : radarSelected && arco && arco.length === 1
                      ? 'Seleziona metrica per Strada'
                      : 'Seleziona metrica per Strada 1'
              }
              options={roadDataOptions}
              value={selectedFactorPaths[0]}
              onChange={(value, selectedOptions) => {
                // Get the last selected option (the actual metric)
                const option = selectedOptions[selectedOptions.length - 1];
                handleRoadDataChange(option, value);
              }}
              getPopupContainer={(trigger) => trigger.parentElement}
              style={{ width: '100%' }}
              expandTrigger="hover"
              displayRender={(labels) => labels.join(' > ')}
            />
          </div>

          {/* Second Select */}
          <div className="dropdown-container">
            <label className="dropdown-label">
              {isComparison
                ? (radarSelected ? 'Multisensor station - Seleziona Metrica' : 'Strada 2 - Seleziona Metrica')
                : isSingleRoad
                  ? 'Strada - Seconda Metrica'
                  : 'Multisensor station - Seconda Metrica'}
            </label>
            <Cascader
              className="confronto-select"
              placeholder={
                isComparison
                  ? (radarSelected ? 'Seleziona metrica per Multisensor station' : 'Seleziona metrica per Strada 2')
                  : 'Seleziona seconda metrica'
              }
              options={sensorDataOptions}
              value={selectedFactorPaths[1]}
              onChange={(value, selectedOptions) => {
                // Get the last selected option (the actual metric)
                const option = selectedOptions[selectedOptions.length - 1];
                handleSensorDataChange(option, value);
              }}
              getPopupContainer={(trigger) => trigger.parentElement}
              style={{ width: '100%' }}
              expandTrigger="hover"
              displayRender={(labels) => labels.join(' > ')}
            />
          </div>
        </div>

        {/* Comparison Chart */}
        <div className="confronto-chart">
          {/* Show info section for single or comparison mode */}
          {(isSingleRoad || isSingleRadar || isComparison) && (
            <div className="comparison-info">
              {/* First element: Road or Radar */}
              {(isSingleRoad || (isComparison && hasRoads)) && (
                <div className="element-info">
                  <h4>{isSingleRoad ? 'Strada' : (radarSelected && arco.length === 1 ? 'Strada' : 'Strada 1')}</h4>
                  <p>Hash: {arco[0]?.hash?.substring(0, 8)}...</p>
                  <p>Geohash: {arco[0]?.geohash}</p>
                  <p>Direzione: {arco[0]?.direction}°</p>
                  <p>Data Points: {arco.filter(e => e.hash === arco[0]?.hash).length}</p>
                </div>
              )}

              {/* Single Radar display */}
              {isSingleRadar && (
                <div className="element-info">
                  <h4>Stazione di monitoraggio</h4>
                  <p>Tipo: Stazione di monitoraggio ambientale</p>
                  <p>Posizione: 44.40838, 8.906963</p>
                  <p>Sensori: CO2, PM2.5, PM10, NO2, Temp, Umidità</p>
                  <p>Dati da: media_giornaliera.json</p>
                </div>
              )}

              {/* Second element for comparison mode */}
              {isComparison && (
                <div className="element-info">
                  {radarSelected ? (
                    <>
                      <h4>Stazione di monitoraggio</h4>
                      <p>Tipo: Stazione di monitoraggio ambientale</p>
                      <p>Posizione: 44.40838, 8.906963</p>
                      <p>Sensori: CO2, PM2.5, PM10, NO2, Temp, Umidità</p>
                      <p>Dati da: media_giornaliera.json</p>
                    </>
                  ) : (
                    <>
                      <h4>Strada 2</h4>
                      <p>Hash: {arco.find(e => e.hash !== arco[0]?.hash)?.hash?.substring(0, 8)}...</p>
                      <p>Geohash: {arco.find(e => e.hash !== arco[0]?.hash)?.geohash}</p>
                      <p>Direzione: {arco.find(e => e.hash !== arco[0]?.hash)?.direction}°</p>
                      <p>Data Points: {arco.filter(e => e.hash !== arco[0]?.hash).length}</p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Chart display */}
          <div style={{ position: 'relative' }}>
            {hasChartData && (
              <button
                onClick={() => setIsExpanded(true)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: '36px',
                  height: '36px',
                  backgroundColor: 'rgba(24, 144, 255, 0.9)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  zIndex: 100,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Espandi grafico"
              >
                <ExpandOutlined />
              </button>
            )}
          {data.length > 0 ? (
            <>
              {/* Comparison mode: two factors required */}
              {isComparison && selectedFactors[0] && selectedFactors[1] ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fontSize: 11, fill: '#666' }}
                      stroke="#ccc"
                      interval="preserveStartEnd"
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" yAxisId="left" />
                    <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" yAxisId="right" orientation="right" />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                    <Line
                      type="monotone"
                      dataKey={selectedFactors[0].field}
                      stroke="#A1E430"
                      strokeWidth={2}
                      dot={false}
                      name={`${radarSelected && arco.length === 1 ? 'Strada' : 'Strada 1'}: ${selectedFactors[0].label}`}
                      yAxisId="left"
                      connectNulls
                    />
                    <Line
                      type="monotone"
                      dataKey={selectedFactors[1].field}
                      stroke="#FF8C42"
                      strokeWidth={2}
                      dot={false}
                      name={`${radarSelected ? 'Radar' : 'Strada 2'}: ${selectedFactors[1].label}`}
                      yAxisId="right"
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : isComparison ? (
                <div className="no-selection">
                  <p>Seleziona due fattori per confrontare i dati nel tempo</p>
                </div>
              ) : null}

              {/* Single selection mode: comparing two metrics from same source */}
              {(isSingleRoad || isSingleRadar) && selectedFactors[0] && selectedFactors[1] ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fontSize: 11, fill: '#666' }}
                      stroke="#ccc"
                      interval="preserveStartEnd"
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" yAxisId="left" />
                    <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" yAxisId="right" orientation="right" />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                    <Line
                      type="monotone"
                      dataKey={selectedFactors[0].field}
                      stroke="#A1E430"
                      strokeWidth={2}
                      dot={false}
                      name={selectedFactors[0].label}
                      yAxisId="left"
                      connectNulls
                    />
                    <Line
                      type="monotone"
                      dataKey={selectedFactors[1].field}
                      stroke="#FF8C42"
                      strokeWidth={2}
                      dot={false}
                      name={selectedFactors[1].label}
                      yAxisId="right"
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (isSingleRoad || isSingleRadar) && selectedFactors[0] ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fontSize: 11, fill: '#666' }}
                      stroke="#ccc"
                      interval="preserveStartEnd"
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                    <Line
                      type="monotone"
                      dataKey={selectedFactors[0].field}
                      stroke="#A1E430"
                      strokeWidth={2}
                      dot={false}
                      name={selectedFactors[0].label}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (isSingleRoad || isSingleRadar) ? (
                <div className="no-selection">
                  <p>Seleziona almeno un fattore da visualizzare</p>
                </div>
              ) : null}
            </>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data} style={{ backgroundColor: '#1a1a1a' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="timestamp" tick={{ fontSize: 11, fill: '#ccc' }} stroke="#444" />
                <YAxis tick={{ fontSize: 11, fill: '#ccc' }} stroke="#444" yAxisId="left" />
                <YAxis tick={{ fontSize: 11, fill: '#ccc' }} stroke="#444" yAxisId="right" orientation="right" />
                <Tooltip contentStyle={{ backgroundColor: '#222', border: '1px solid #444', color: '#fff' }} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#ccc' }} iconType="line" />

                {selectedFactors[0] && (
                  <Line
                    type="monotone"
                    dataKey={selectedFactors[0].field || selectedFactors[0].key}
                    stroke="#A1E430"
                    strokeWidth={2}
                    dot={false}
                    name={selectedFactors[0].label}
                    yAxisId="left"
                  />
                )}
                {selectedFactors[1] && (
                  <Line
                    type="monotone"
                    dataKey={selectedFactors[1].field || selectedFactors[1].key}
                    stroke="#FF8C42"
                    strokeWidth={2}
                    dot={false}
                    name={selectedFactors[1].label}
                    yAxisId="right"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
          </div>
        </div>
      </div>
    </div>

    {/* Expanded Chart Modal */}
    <Modal
      open={isExpanded}
      onCancel={() => setIsExpanded(false)}
      footer={null}
      width="95%"
      centered
      title="Confronto - Vista Espansa"
      destroyOnClose
      zIndex={9999}
      style={{ top: 20 }}
    >
      <div style={{ padding: '20px 0' }}>
        {data.length > 0 && (isComparison && selectedFactors[0] && selectedFactors[1]) && (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="timestamp"
                tick={{ fontSize: 11, fill: '#666' }}
                stroke="#ccc"
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" yAxisId="left" />
              <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" yAxisId="right" orientation="right" />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

              <Line
                type="monotone"
                dataKey={selectedFactors[0].field}
                stroke="#A1E430"
                strokeWidth={2}
                dot={false}
                name={`${radarSelected && arco.length === 1 ? 'Strada' : 'Strada 1'}: ${selectedFactors[0].label}`}
                yAxisId="left"
                connectNulls
              />
              <Line
                type="monotone"
                dataKey={selectedFactors[1].field}
                stroke="#FF8C42"
                strokeWidth={2}
                dot={false}
                name={`${radarSelected ? 'Radar' : 'Strada 2'}: ${selectedFactors[1].label}`}
                yAxisId="right"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {data.length > 0 && (isSingleRoad || isSingleRadar) && selectedFactors[0] && selectedFactors[1] && (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="timestamp"
                tick={{ fontSize: 11, fill: '#666' }}
                stroke="#ccc"
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" yAxisId="left" />
              <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" yAxisId="right" orientation="right" />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

              <Line
                type="monotone"
                dataKey={selectedFactors[0].field}
                stroke="#A1E430"
                strokeWidth={2}
                dot={false}
                name={selectedFactors[0].label}
                yAxisId="left"
                connectNulls
              />
              <Line
                type="monotone"
                dataKey={selectedFactors[1].field}
                stroke="#FF8C42"
                strokeWidth={2}
                dot={false}
                name={selectedFactors[1].label}
                yAxisId="right"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {data.length > 0 && (isSingleRoad || isSingleRadar) && selectedFactors[0] && !selectedFactors[1] && (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="timestamp"
                tick={{ fontSize: 11, fill: '#666' }}
                stroke="#ccc"
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

              <Line
                type="monotone"
                dataKey={selectedFactors[0].field}
                stroke="#A1E430"
                strokeWidth={2}
                dot={false}
                name={selectedFactors[0].label}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </Modal>
    </>
  );
};

export default ConfrontoIMUGraph;
